import { SearchComponent } from "../../../../components/components/js/SearchComponent";
import { HeaderComponent } from "../../../../components/components/js/HeaderComponent";
import Pagination from "../../../../components/general/Pagination";
import { LoadingDots } from "../../../../components/general";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import AuthContext from "../../../../context/AuthProvider";
import { useState, useEffect, useCallback, useContext } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import API_URLS from "../../../../constants/apiConstants";
import AddDocumentModal from "./components/AddDocumentModal";
import { DocumentCards } from "./components/DocumentCard";
import { useTheme } from "@emotion/react";

export const Documents = () => {
  const { t, i18n } = useTranslation(); 
  const isAr = i18n.language === 'ar';
  const { school } = useContext(AuthContext);
  const axiosPrivate = useAxiosPrivate();
  const muiTheme = useTheme();

  // Data States
  const [documentGroups, setDocumentGroups] = useState([]);
  const [roomOptions, setRoomOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // Filter States
  const [audienceFilter, setAudienceFilter] = useState("");
  const [roomFilter, setRoomFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const PAGE_SIZE = 10;

  // Fetch rooms on mount
  useEffect(() => {
    if (school?.id) {
      axiosPrivate.get(API_URLS.getSchoolRooms(school.id))
        .then(res => {
          const options = res.data.map(room => ({ 
            value: room.id, 
            label: room.name 
          }));
          setRoomOptions(options);
        })
        .catch(err => console.error("Error fetching rooms:", err));
    }
  }, [school?.id, axiosPrivate]);

  // Fetch logic with search and pagination
  const fetchDocuments = useCallback(async (page = 1) => {
    if (!school?.id) return;
    
    setLoading(true);
    try {
      // Build URL with filters
      let url = `${API_URLS.getDocumentGroups(school.id, audienceFilter, roomFilter, page)}&page_size=${PAGE_SIZE}`;
      
      // Add title search if present
      if (searchQuery) {
        url += `&title=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await axiosPrivate.get(url);
      
      setDocumentGroups(response.data.results);
      setTotalCount(response.data.total_items || response.data.count);
      setTotalPages(response.data.total_pages || 1); 
      setNextPage(response.data.links?.next || response.data.next);
      setPreviousPage(response.data.links?.previous || response.data.previous);
      setCurrentPage(page);
    } catch (error) {
      toast.error(isAr ? "فشل في تحميل الملفات" : "Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, school?.id, audienceFilter, roomFilter, searchQuery, t, PAGE_SIZE]);

  const handleDelete = async (document) => {
    try {
      await axiosPrivate.delete(API_URLS.deleteDocumentGroup(document.id));
      toast.success(isAr ? "تم حذف الملف بنجاح" : "Document deleted successfully");
      fetchDocuments(currentPage);
    } catch (error) {
      console.error("Delete Error:", error);
      toast.error(isAr ? "فشل في حذف الملف" : "Failed to delete document");
    }
  };

  const handleEdit = (document) => {
    fetchDocuments(currentPage);
  };

  // Debounced search - triggers 500ms after user stops typing
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery !== undefined) {
        fetchDocuments(1);
      }
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Fetch when filters change (non-debounced)
  useEffect(() => {
    fetchDocuments(1);
  }, [audienceFilter, roomFilter]);

  // Styles and Options
  const audienceOptions = [
    { value: "", label: t("audience.all") || "All" },
    { value: "staff", label: t("audience.staff") || "Staff" },
    { value: "parents", label: t("audience.parents") || "Parents" },
  ];

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      boxShadow: "none",
      cursor: "pointer",
      minHeight: "40px",
      backgroundColor: "#FCFAFA",
      borderRadius: "4px",
    }),
    menu: (base) => ({
      ...base,
      marginTop: "4px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? muiTheme.palette.primary.main
        : state.isFocused
        ? muiTheme.palette.primary.main + "20"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      padding: "8px 12px",
    }),
  };

  return (
    <div className="container-fluid px-4">
      <HeaderComponent 
        title="documents" 
        buttonName="add_document" 
        setShow={setShowAddModal} 
      />

      <div className="d-flex align-items-center gap-2 mb-3">
        <div style={{ flex: 1 }}>
          <SearchComponent
            items={[{ value: "title", label: t("search.title") }]}
            onSearch={(val) => setSearchQuery(val)}
            placeholder="search_by_title"
          />
        </div>
        <div style={{ width: "180px" , height: "60px" }}>
          <Select
            options={audienceOptions}
            placeholder={t("audience.filter")}
            isClearable
            styles={customSelectStyles}
            onChange={(opt) => setAudienceFilter(opt?.value || "")}
            isSearchable={false}
          />
        </div>
       <div style={{ width: "180px" , height: "60px" }}>
          <Select
            options={roomOptions}
            placeholder={isAr ? " الفصل" : " Room"}
            isClearable
            isSearchable={false}
            styles={customSelectStyles}
            onChange={(opt) => setRoomFilter(opt?.value || "")}
          />
        </div>
      </div>

      {loading ? <LoadingDots /> : (
        <div>
          <DocumentCards 
            documents={documentGroups}
            onDelete={handleDelete}
            onEdit={handleEdit}
          />

          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            nextPage={nextPage}
            previousPage={previousPage}
            onPageChange={(page) => fetchDocuments(page)}
            pageSize={PAGE_SIZE} 
          />
        </div>
      )}

      <AddDocumentModal 
        open={showAddModal} 
        onClose={() => setShowAddModal(false)} 
        onSuccess={() => fetchDocuments(1)}
      />
    </div>
  );
};